import { apiConfig } from '../../authConfig';
import { apiErrorFromResponse } from '../../utils/apiError';

/**
 * Control Taxonomy API (platform-admin only). One thin request helper so every call shares auth,
 * the `{success,data,meta}` envelope and ApiError handling.
 */
async function request(getToken, path, { method = 'GET', query, body } = {}) {
  const token = await getToken();
  const qs = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const url = `${apiConfig.baseUrl}/api/v1${path}${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.success) throw apiErrorFromResponse(res, json);
  return { data: json.data, meta: json.meta || {} };
}

/** Message for a failed call, adding invalid refs when a 400 lists them in `details`. */
export function errorText(err, fallback) {
  const base = (err && err.message) || fallback;
  const d = err && err.details;
  const list = d && (d.invalidRefs || d.invalid || d.invalidTargetRefs);
  return Array.isArray(list) && list.length && !base.includes(String(list[0])) ? `${base} Invalid: ${list.join(', ')}` : base;
}

const asList = (data, key) => (Array.isArray(data) ? data : Array.isArray(data?.[key]) ? data[key] : Array.isArray(data?.rows) ? data.rows : []);

export const taxonomyApi = (getToken) => ({
  async frameworks() {
    const { data } = await request(getToken, '/control-taxonomy/frameworks');
    return Array.isArray(data) ? data : [];
  },
  async matrix({ frameworks, search, page, pageSize }) {
    const { data, meta } = await request(getToken, '/control-taxonomy/mapping-matrix', { query: { frameworks: (frameworks || []).join(','), search, page, pageSize } });
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    return { rows, total: typeof meta.total === 'number' ? meta.total : rows.length };
  },
  async saveCell(isoControl, frameworkKey, targetRefs) {
    const { data } = await request(getToken, `/control-taxonomy/mapping-matrix/${encodeURIComponent(isoControl)}/${encodeURIComponent(frameworkKey)}`, { method: 'PUT', body: { targetRefs } });
    return data;
  },
  async unmapped({ framework, search, page, pageSize }) {
    const { data, meta } = await request(getToken, '/control-taxonomy/unmapped', { query: { framework, search, page, pageSize } });
    const rows = asList(data, 'controls').map((r) => ({
      ref: r.targetRef ?? r.ctrlNumber ?? r.ref,
      name: r.targetName ?? r.ctrlName ?? r.name ?? '',
    })).filter((r) => r.ref);
    return { rows, total: typeof meta.total === 'number' ? meta.total : rows.length };
  },
  async pendingCount() {
    const { data } = await request(getToken, '/control-taxonomy/pending-count');
    return { iso27001: Number(data?.iso27001) || 0, frameworks: data?.frameworks || {}, total: Number(data?.total) || 0 };
  },
  async bulkAssign(frameworkKey, assignments) {
    const { data } = await request(getToken, '/control-taxonomy/mappings/bulk-assign', { method: 'POST', body: { frameworkKey, assignments } });
    return data;
  },
  /** Targets for a framework's cell editor: the latest Policy Hub version's controls. */
  async frameworkControls(frameworkKey) {
    const { data } = await request(getToken, `/frameworks/${encodeURIComponent(frameworkKey)}/controls`);
    return (Array.isArray(data?.controls) ? data.controls : []).map((c) => ({ ref: c.ctrlNumber, name: c.ctrlName || '' })).filter((c) => c.ref);
  },
  /** ISO 27002 controls for the pending-tab picker, labelled the way the matrix labels them (A.x.y). */
  async isoControls() {
    const { data } = await request(getToken, '/iso27001/controls');
    return (Array.isArray(data?.controls) ? data.controls : []).map((c) => {
      const t = String(c.ctrlNumber || '').trim();
      return { ref: /^A\./i.test(t) ? t : `A.${t}`, name: c.ctrlName || '' };
    }).filter((c) => c.ref !== 'A.');
  },
});
